import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import { Advertiser, Keyword } from '../types';

const AdvertiserList: React.FC = () => {
  const [search, setSearch]                   = useState('');
  const [platform, setPlatform]               = useState('');
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [dropdownOpen, setDropdownOpen]       = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // ドロップダウン外クリックで閉じる
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // 登録キーワード取得
  const { data: keywordsData } = useQuery({
    queryKey: ['keywords'],
    queryFn: async () => (await api.get('/keywords')).data,
  });
  const registeredKeywords: Keyword[] = keywordsData?.keywords || [];

  // 広告主一覧取得（キーワードはクライアントフィルター）
  const { data, isLoading } = useQuery({
    queryKey: ['advertisers', search, platform],
    queryFn: async () => {
      const res = await api.get('/advertisers', { params: { search, platform } });
      return res.data;
    },
  });

  // クライアント側キーワードフィルター
  const advertisers: Advertiser[] = useMemo(() => {
    const all: Advertiser[] = data?.advertisers || [];
    if (selectedKeywords.length === 0) return all;
    return all.filter(adv => selectedKeywords.includes(adv.keyword || ''));
  }, [data, selectedKeywords]);

  const toggleKeyword = (kw: string) =>
    setSelectedKeywords(prev =>
      prev.includes(kw) ? prev.filter(k => k !== kw) : [...prev, kw]
    );

  const selectAll   = () => setSelectedKeywords(registeredKeywords.map(k => k.keyword));
  const deselectAll = () => setSelectedKeywords([]);

  const resetMutation = useMutation({
    mutationFn: async () => {
      const res = await api.delete('/advertisers');
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['advertisers'] });
      alert(`✅ ${data.deleted}件削除しました`);
    },
    onError: () => {
      alert('削除に失敗しました');
    },
  });

  const handleReset = () => {
    if (window.confirm('広告主リストを全件削除しますか？\nこの操作は取り消せません。')) {
      resetMutation.mutate();
    }
  };

  const handleExport = async () => {
    const params: Record<string, string> = { search, platform };
    if (selectedKeywords.length === 1) params.keyword = selectedKeywords[0];
    const res = await api.get('/advertisers/export', { params, responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a   = document.createElement('a');
    a.href    = url;
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    a.download = selectedKeywords.length === 1
      ? `adradar_${selectedKeywords[0]}_${date}.xlsx`
      : `adradar_${date}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <h1 className="page-title">広告主リスト</h1>

      <div className="card" style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* テキスト検索 */}
        <input
          className="input"
          placeholder="会社名・URLで検索..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 240 }}
        />

        {/* キーワードチェックボックスドロップダウン */}
        <div ref={dropdownRef} style={{ position: 'relative' }}>
          <button
            className="btn btn-secondary"
            onClick={() => setDropdownOpen(o => !o)}
            style={{ minWidth: 160, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
          >
            <span>
              {selectedKeywords.length === 0
                ? 'キーワード絞り込み'
                : `${selectedKeywords.length}件選択中`}
            </span>
            <span style={{ fontSize: 10 }}>{dropdownOpen ? '▲' : '▼'}</span>
          </button>

          {dropdownOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 100,
              background: '#1e2235', border: '1px solid #2d3348', borderRadius: 8,
              padding: 12, minWidth: 200, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            }}>
              {/* 全選択 / 全解除 */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <button
                  className="btn btn-secondary"
                  style={{ flex: 1, fontSize: 12, padding: '4px 0' }}
                  onClick={selectAll}
                >
                  全選択
                </button>
                <button
                  className="btn btn-secondary"
                  style={{ flex: 1, fontSize: 12, padding: '4px 0' }}
                  onClick={deselectAll}
                >
                  全解除
                </button>
              </div>

              {registeredKeywords.length === 0 ? (
                <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>キーワードなし</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {registeredKeywords.map(kw => (
                    <label
                      key={kw.keyword_id}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedKeywords.includes(kw.keyword)}
                        onChange={() => toggleKeyword(kw.keyword)}
                        style={{ accentColor: '#4f6ef7', width: 15, height: 15 }}
                      />
                      <span style={{ color: kw.enabled ? '#e2e8f0' : '#475569' }}>
                        {kw.keyword}
                        {!kw.enabled && <span style={{ fontSize: 11, marginLeft: 4 }}>(無効)</span>}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 選択中キーワードのバッジ */}
        {selectedKeywords.map(kw => (
          <span
            key={kw}
            className="badge badge-blue"
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
            onClick={() => toggleKeyword(kw)}
          >
            {kw} ✕
          </span>
        ))}

        {/* プラットフォーム */}
        <select
          className="input"
          value={platform}
          onChange={e => setPlatform(e.target.value)}
          style={{ maxWidth: 160 }}
        >
          <option value="">全プラットフォーム</option>
          <option value="facebook">Facebook</option>
          <option value="instagram">Instagram</option>
        </select>

        <span style={{ color: '#64748b', fontSize: 14 }}>{advertisers.length}件</span>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button
            className="btn btn-secondary"
            onClick={handleExport}
          >
            📥 Excelエクスポート
          </button>
          <button
            className="btn btn-danger"
            onClick={handleReset}
            disabled={resetMutation.isPending}
            style={{ fontSize: 13 }}
          >
            {resetMutation.isPending ? '削除中...' : '🗑️ データリセット'}
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {isLoading ? (
          <div className="loading">読み込み中...</div>
        ) : advertisers.length === 0 ? (
          <div className="empty-state">
            <div className="icon">🏢</div>
            <div>広告主データがありません</div>
            <div style={{ fontSize: 13, marginTop: 8, color: '#475569' }}>
              スクレイピングを実行してデータを収集してください
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>広告主名</th>
                  <th>キーワード</th>
                  <th>PF</th>
                  <th>電話番号</th>
                  <th>メール</th>
                  <th>サイトURL</th>
                  <th>Facebook</th>
                  <th>特商法</th>
                  <th>SS</th>
                  <th>取得日</th>
                </tr>
              </thead>
              <tbody>
                {advertisers.map(adv => (
                  <tr key={adv.advertiser_id}>
                    <td>
                      <div style={{ fontWeight: 600, color: '#e2e8f0' }}>
                        {adv.company_name || adv.fb_page_name || '-'}
                      </div>
                      {adv.representative && (
                        <div style={{ fontSize: 12, color: '#64748b' }}>{adv.representative}</div>
                      )}
                    </td>
                    <td><span className="badge badge-blue">{adv.keyword || '-'}</span></td>
                    <td>
                      <span style={{ fontSize: 12 }}>
                        {(adv as any).platform === 'instagram' ? '📸' : '📘'}
                      </span>
                    </td>
                    <td style={{ fontSize: 13 }}>{adv.phone || '-'}</td>
                    <td style={{ fontSize: 12 }}>{adv.email || '-'}</td>
                    <td>
                      {adv.site_url ? (
                        <a href={adv.site_url} target="_blank" rel="noreferrer"
                          style={{ color: '#4f6ef7', fontSize: 12 }}>
                          {adv.site_url.replace(/^https?:\/\//, '').slice(0, 20)}...
                        </a>
                      ) : '-'}
                    </td>
                    <td>
                      {(adv as any).fb_url ? (
                        <a href={(adv as any).fb_url} target="_blank" rel="noreferrer"
                          style={{ color: '#4f6ef7', fontSize: 12 }}>FB</a>
                      ) : '-'}
                    </td>
                    <td>
                      {adv.tokushoho_url ? (
                        <a href={adv.tokushoho_url} target="_blank" rel="noreferrer">
                          <span className="badge badge-green">あり</span>
                        </a>
                      ) : (
                        <span className="badge badge-gray">なし</span>
                      )}
                    </td>
                    <td>
                      {(adv as any).screenshot_url ? (
                        <a href={(adv as any).screenshot_url} target="_blank" rel="noreferrer"
                          style={{ color: '#4f6ef7', fontSize: 12 }}>📷</a>
                      ) : '-'}
                    </td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>
                      {adv.scraped_at?.slice(0, 10) || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvertiserList;
